import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useSupabase } from '../contexts/SupabaseContext';
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

interface FormErrors {
  email?: string;
}

const MagicLinkRequest: React.FC = () => {
  const navigate = useNavigate();
  const supabase = useSupabase();

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // For the first step - requesting password reset email
  const handleRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsSubmitting(true);

    try {
      // Validate email
      if (!email) {
        setErrors({ email: 'Email is required' });
        setIsSubmitting(false);
        return;
      }

      // First check if the email exists in the system using the check-email edge function
      const checkEmailUrl = `${SUPABASE_URL}/functions/v1/check-email`;
      const checkResponse = await fetch(checkEmailUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ email })
      });

      const checkResult = await checkResponse.json();

      // If the email doesn't exist in the system, show an error message directing to sign up
      if (checkResult.success && !checkResult.exists) {
        setSubmitError('This email is not registered. Please sign up for an account first at https://app.timelyirb.com/signup.');
        setIsSubmitting(false);
        return;
      }

      // Send magic link instead of password reset
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        }
      });

      if (error) {
        throw error;
      }

      setSubmitSuccess('Check your email for a magic link.');
    } catch (error) {
      console.error('Error requesting magic link:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'An error occurred while requesting magic link'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Marketing Side - matches existing AuthPage */}
      <div className="hidden md:flex md:w-1/3 bg-[#F55D3E] items-center justify-center p-12">
        <div className="max-w-md text-white space-y-8">
          <h1 className="text-4xl font-bold">
            IRB reviews in 12 business hours or less, guaranteed.
          </h1>
        </div>
      </div>

      {/* Auth Side */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Logo className="mx-auto mb-8" externalUrl="https://timelyirb.com" />

          <h1 className="text-2xl font-bold mb-8 text-center">
            Request Magic Link
          </h1>

          {submitError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {submitSuccess}
            </div>
          )}

          <p className="text-center mb-6 text-gray-600">
            Enter your email and we will send you a magic link.<br /> If you aren't signed up with Timely yet, you must first create an account <Link to="/signup" className="text-[#F55D3E] hover:text-[#F9A594]">here</Link>.
          </p>

          <form className="space-y-4" onSubmit={handleRequestMagicLink}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${errors.email ? 'border-red-500' : ''
                  }`}
                required
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-[#F55D3E] text-white py-2 px-4 rounded-lg hover:bg-[#F9A594] transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              {isSubmitting ? 'Please wait...' : 'Send Magic Link'}
            </button>
          </form>

          <p className="mt-4 text-center">
            <button
              onClick={() => navigate('/signin')}
              className="text-[#F55D3E] hover:text-[#F9A594] text-sm"
            >
              Back to Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MagicLinkRequest;
